import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, TextField, Button, Box, CircularProgress, Grid2, Alert, InputLabel, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Select, MenuItem, FormControl,
  IconButton as MuiIconButton, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../Navbar';
import Footer from '../Footer';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhoneInput from 'react-phone-number-input';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { CSVLink } from 'react-csv';
import jsPDF from 'jspdf';
import 'react-phone-number-input/style.css';
import autoTable from 'jspdf-autotable';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ContractIcon from '@mui/icons-material/Assignment';
import ContractEditor from './ContractEditor';
import AddIcon from '@mui/icons-material/Add';
import Bills from './Bills';
import Tenant from './Tenant';
import Income from './Income';

const currencyOptions = [
  { value: 'USD', label: 'US Dollar (USD)' },
  { value: 'EUR', label: 'Euro (EUR)' },
  { value: 'GBP', label: 'British Pound (GBP)' },
  { value: 'JPY', label: 'Japanese Yen (JPY)' },
  { value: 'CAD', label: 'Canadian Dollar (CAD)' },
  { value: 'AUD', label: 'Australian Dollar (AUD)' },
  { value: 'INR', label: 'Indian Rupee (INR)' },
  { value: 'PLN', label: 'Polish Zloty (PLN)' },
];

const RentHistory = () => {
  const { apartmentId } = useParams();
  const navigate = useNavigate();
  const [rentHistories, setRentHistories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState('');
  const [placeDescription, setPlaceDescription] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [openContractModal, setOpenContractModal] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [contractVersions, setContractVersions] = useState({});
  const [openDialog, setOpenDialog] = useState(false);

  const [newRentHistory, setNewRentHistory] = useState({
    id: 0,
    rent_start: '',
    rent_end: '',
    rent_currency: 'USD',
    rent_amount: 0,
    tenant_name: '',
    tenant_email: '',
    tenant_phone: '',
    tenancy_agreement: null
  });

  useEffect(() => {
    const savedVersions = JSON.parse(localStorage.getItem(`contractVersions_${apartmentId}`)) || {};
    setContractVersions(savedVersions);
  }, [apartmentId]);

  const handleContractUpdate = (historyId, updatedText) => {
    setContractVersions(prev => {
      const newVersions = { ...prev, [historyId]: updatedText };
      localStorage.setItem(`contractVersions_${apartmentId}`, JSON.stringify(newVersions));
      return newVersions;
    });
  };

  const csvHeaders = [
    { label: "Start Date", key: "rent_start" },
    { label: "End Date", key: "rent_end" },
    { label: "Currency", key: "rent_currency" },
    { label: "Amount", key: "rent_amount" },
    { label: "Tenant Name", key: "tenant_name" },
    { label: "Tenant Email", key: "tenant_email" },
    { label: "Tenant Phone", key: "tenant_phone" },
  ];

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    const storedToken = userData?.token || '';
    setToken(storedToken);
  }, []);

  const fetchRentHistories = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/renthistories-apartment/${apartmentId}/`, {
        headers: { 'Authorization': `Token ${token}` }
      });
      setRentHistories(response.data);
    } catch (error) {
      setError('Failed to fetch rent histories.');
      console.error('Error fetching rent histories:', error);
    } finally {
      setLoading(false);
    }
  }, [apartmentId, token]);

  useEffect(() => {
    const fetchApartmentDetails = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/apartments/${apartmentId}/`, {
          headers: { 'Authorization': `Token ${token}` }
        });
        setPlaceDescription(response.data.place_description || '');
      } catch (error) {
        console.error('Error fetching apartment details:', error);
      }
    };
    fetchApartmentDetails();
  }, [apartmentId, token]);

  useEffect(() => {
    fetchRentHistories();
  }, [fetchRentHistories]);

  const handleChange = (name, value) => {
    setNewRentHistory(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleEdit = (history) => {
    setIsEditing(true);
    setNewRentHistory({
      ...history,
      tenancy_agreement: null
    });
    setOpenDialog(true);
  };

  const handleDelete = async (historyId) => {
    if (window.confirm('Are you sure you want to delete this rent history?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/api/renthistories/${historyId}/`, {
          headers: { 'Authorization': `Token ${token}` }
        });
        alert('Rent history deleted successfully');
        setContractVersions(prev => {
          const newVersions = { ...prev };
          delete newVersions[historyId];
          localStorage.setItem(`contractVersions_${apartmentId}`, JSON.stringify(newVersions));
          return newVersions;
        });
        fetchRentHistories();
      } catch (error) {
        console.error('Error deleting rent history:', error);
        setError('Failed to delete rent history.');
      }
    }
  };

  const handleOpenContract = (history) => {
    setSelectedHistory(history);
    setOpenContractModal(true);
  };

  const handleCloseContract = () => {
    setOpenContractModal(false);
    setSelectedHistory(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('handleSubmit triggered with data:', newRentHistory); // Debug log

    const formDataToSend = new FormData();
    formDataToSend.append('apartment', apartmentId);

    Object.keys(newRentHistory).forEach(field => {
      if (field === 'tenancy_agreement' && newRentHistory[field]) {
        formDataToSend.append(field, newRentHistory[field]);
      } else if (newRentHistory[field] !== '' && newRentHistory[field] !== undefined && field !== 'tenancy_agreement') {
        formDataToSend.append(field, newRentHistory[field]);
      }
    });

    try {
      if (isEditing) {
        console.log('Updating rent history with ID:', newRentHistory.id); // Debug log
        await axios.put(`${process.env.REACT_APP_API_URL}/api/renthistories/${newRentHistory.id}/`, formDataToSend, {
          headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        alert('Rent history updated successfully');
      } else {
        console.log('Creating new rent history'); // Debug log
        await axios.post(`${process.env.REACT_APP_API_URL}/api/renthistories/`, formDataToSend, {
          headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        alert('Rent history created successfully');
      }
      resetForm();
      setOpenDialog(false);
      fetchRentHistories();
    } catch (error) {
      console.error('Error submitting rent history:', error.response?.data || error.message); // Debug log
      setError(`Failed to ${isEditing ? 'update' : 'create'} rent history: ${error.response?.data?.detail || error.message}`);
    }
  };

  const resetForm = () => {
    setNewRentHistory({
      id: 0,
      rent_start: '',
      rent_end: '',
      rent_currency: 'USD',
      rent_amount: 0,
      tenant_name: '',
      tenant_email: '',
      tenant_phone: '',
      tenancy_agreement: null
    });
    setIsEditing(false);
    setError(null);
  };

  const handleOpenDialog = () => {
    resetForm();
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    resetForm();
    setOpenDialog(false);
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(`Rent History for Apartment ${placeDescription}`, 14, 20);
    doc.setFontSize(12);
    doc.text(placeDescription || 'No description available', 14, 30);

    const tableColumn = ["Start Date", "End Date", "Currency", "Amount", "Tenant Name", "Email", "Phone", "Agreement"];
    const tableRows = rentHistories.map(history => [
      history.rent_start || '',
      history.rent_end || '',
      history.rent_currency || '',
      history.rent_amount.toString() || '0',
      history.tenant_name || '',
      history.tenant_email || '',
      history.tenant_phone || '',
      history.tenancy_agreement ? 'Yes' : 'No'
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 40,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [66, 66, 66] },
      alternateRowStyles: { fillColor: [240, 240, 240] },
    });

    doc.save(`rent_history_${placeDescription}.pdf`);
  };

  // Determine current tenant
  const today = new Date();
  const currentTenant = rentHistories.find(history => {
    const startDate = new Date(history.rent_start);
    const endDate = new Date(history.rent_end);
    return startDate <= today && today <= endDate;
  });

  return (
    <>
      <Navbar />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <MuiIconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </MuiIconButton>
          <Typography variant="h4" component="h1">
            Rent History
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 4 }}>
              <Tenant currentTenant={rentHistories[0]} placeDescription={placeDescription} token={token} />
            </Box>

            <Box sx={{ mb: 4 }}>
              <Bills apartmentId={apartmentId} token={token} />
            </Box>

            {/* Rent History Table */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Rental History
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Start Date</TableCell>
                      <TableCell>End Date</TableCell>
                      <TableCell>Tenant Name</TableCell>
                      <TableCell>Tenant Email</TableCell>
                      <TableCell>Tenant Phone</TableCell>
                      <TableCell>Currency</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rentHistories.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center">No rent histories available.</TableCell>
                      </TableRow>
                    ) : (
                      rentHistories.map((history, index) => (
                        <TableRow key={index}>
                          <TableCell>{history.rent_start}</TableCell>
                          <TableCell>{history.rent_end}</TableCell>
                          <TableCell>{history.tenant_name}</TableCell>
                          <TableCell>{history.tenant_email}</TableCell>
                          <TableCell>{history.tenant_phone}</TableCell>
                          <TableCell>{history.rent_currency}</TableCell>
                          <TableCell>{history.rent_amount}</TableCell>
                          <TableCell>
                            <MuiIconButton 
                              color="primary" 
                              onClick={() => handleEdit(history)}
                            >
                              <EditIcon />
                            </MuiIconButton>
                            <MuiIconButton 
                              color="error" 
                              onClick={() => handleDelete(history.id)}
                            >
                              <DeleteIcon />
                            </MuiIconButton>
                            <MuiIconButton
                              color="secondary"
                              onClick={() => handleOpenContract(history)}
                              title="Generate Contract"
                            >
                              <ContractIcon />
                            </MuiIconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <MuiIconButton
                  onClick={handleDownloadPDF}
                  disabled={rentHistories.length === 0}
                  color="primary"
                  title="Download as PDF"
                >
                  <PictureAsPdfIcon />
                </MuiIconButton>
                <CSVLink
                  data={rentHistories}
                  headers={csvHeaders}
                  filename={`rent_history_${placeDescription}.csv`}
                  style={{ textDecoration: 'none' }}
                >
                  <MuiIconButton
                    disabled={rentHistories.length === 0}
                    color="primary"
                    title="Download as CSV"
                  >
                    <DescriptionIcon />
                  </MuiIconButton>
                </CSVLink>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleOpenDialog}
                >
                  Add New Rental
                </Button>
              </Box>
            </Box>
            
            {/* Income Report Section */}
            <Box>
              <Income rentHistories={rentHistories} />
            </Box>

          </>
        )}
      </Container>
      <Footer />

      {/* Dialog for Add/Edit Rental */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Rental' : 'Add New Rental'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <form onSubmit={handleSubmit}>
            <Grid2 container spacing={3}>
              <Grid2 item xs={12} sm={6} md={4}>
                <TextField
                  label="Rent Start"
                  name="rent_start"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={newRentHistory.rent_start}
                  onChange={(e) => handleChange('rent_start', e.target.value)}
                  fullWidth
                  margin="normal"
                />
              </Grid2>
              <Grid2 item xs={12} sm={6} md={4}>
                <TextField
                  label="Rent End"
                  name="rent_end"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={newRentHistory.rent_end}
                  onChange={(e) => handleChange('rent_end', e.target.value)}
                  fullWidth
                  margin="normal"
                />
              </Grid2>
              <Grid2 item xs={12} sm={6} md={4}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="rent-currency-label">Rent Currency</InputLabel>
                  <Select
                    labelId="rent-currency-label"
                    name="rent_currency"
                    value={newRentHistory.rent_currency || 'USD'}
                    onChange={(e) => handleChange('rent_currency', e.target.value)}
                    label="Rent Currency"
                  >
                    {currencyOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>
              <Grid2 item xs={12} sm={6} md={4}>
                <TextField
                  label="Rent Amount"
                  name="rent_amount"
                  type="number"
                  value={newRentHistory.rent_amount || 0}
                  onChange={(e) => handleChange('rent_amount', e.target.value)}
                  fullWidth
                  margin="normal"
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid2>
              <Grid2 item xs={12} sm={6} md={4}>
                <TextField
                  name="tenant_name"
                  label="Tenant Name"
                  value={newRentHistory.tenant_name}
                  onChange={(e) => handleChange('tenant_name', e.target.value)}
                  fullWidth
                  margin="normal"
                />
              </Grid2>
              <Grid2 item xs={12} sm={6} md={4}>
                <TextField
                  name="tenant_email"
                  label="Tenant Email"
                  type="email"
                  value={newRentHistory.tenant_email}
                  onChange={(e) => handleChange('tenant_email', e.target.value)}
                  fullWidth
                  margin="normal"
                />
              </Grid2>
              <Grid2 item xs={12} sm={6} md={4}>
                <Box sx={{ marginTop: 2 }}>
                  <InputLabel htmlFor="tenant_phone">Tenant Phone</InputLabel>
                  <PhoneInput
                    name="tenant_phone"
                    value={newRentHistory.tenant_phone || ''}
                    onChange={(value) => handleChange('tenant_phone', value)}
                    defaultCountry="US"
                    international
                    countryCallingCodeEditable={false}
                    style={{
                      width: '100%',
                      padding: '16.5px 14px',
                      fontSize: '1rem',
                      borderRadius: '4px',
                      border: '1px solid rgba(0, 0, 0, 0.23)',
                    }}
                  />
                </Box>
              </Grid2>
            </Grid2>
            <DialogActions>
              <Button onClick={handleCloseDialog} color="secondary">
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                {isEditing ? 'Update' : 'Add'}
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      <ContractEditor
        open={openContractModal}
        onClose={handleCloseContract}
        rentalData={selectedHistory}
        placeDescription={placeDescription}
        contractText={selectedHistory ? contractVersions[selectedHistory.id] : undefined}
        onContractUpdate={(text) => handleContractUpdate(selectedHistory?.id, text)}
      />
    </>
  );
};

export default RentHistory;